import { useCTW } from '@ctw/shared/context/ctw-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { getAiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface BuilderUsageAttributes {
  month: string;
  freeSummariesGenerated: number;
  freeSummariesDenied: number;
  includedSummariesGenerated: number;
  meteredSummariesGenerated: number;
}

interface BuilderApiResponse {
  data: {
    type: 'builders';
    id: string;
    attributes: {
      name: string;
      billingTier: string;
      monthlyLimit: number;
      createdAt: string;
      updatedAt: string;
    };
    relationships: {
      defaultConfig?: {
        data: { type: string; id: string };
      };
      configs?: {
        data: Array<{ type: string; id: string }>;
      };
      usage?: {
        data: Array<{ type: 'usage'; userId: string }>;
      };
    };
  };
  included?: Array<
    | {
        type: 'configs';
        id: string;
        attributes: Record<string, unknown>;
      }
    | {
        type: 'usage';
        userId: string;
        attributes: BuilderUsageAttributes;
      }
  >;
}

export interface UseBuilderUsageResult {
  monthlyLimit: number;
  summariesUsed: number;
  billingTier: string;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * @param enabled When false, no usage is fetched (used to suppress all GPS network activity for
 * builders with GPS disabled). The hook still returns a stable, inert result.
 */
export function useBuilderUsage(enabled = true): UseBuilderUsageResult {
  const { requestContext } = useCTW();
  const telemetry = useTelemetry();
  const [data, setData] = useState<BuilderApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const apiUrl = requestContext.builderId
    ? `${getAiServiceBaseUrl({ env: requestContext.env })}/builder/${requestContext.builderId}?include=usage`
    : null;

  const hasFetchedRef = useRef(false);

  const fetchUsage = useCallback(
    async (signal?: AbortSignal) => {
      if (!apiUrl) {
        setIsLoading(false);
        setError(new Error('Builder ID is required.'));
        return;
      }

      // Only show loading state on initial fetch, not on refetches,
      // to avoid unmounting children that depend on this data.
      if (!hasFetchedRef.current) {
        setIsLoading(true);
      }
      setError(null);
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${requestContext.authToken}`,
            'Zus-Account': requestContext.builderId,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error fetching builder usage: ${response.status}`);
        }

        const json: BuilderApiResponse = await response.json();
        setData(json);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        const fetchError =
          err instanceof Error ? err : new Error(`Unknown error fetching builder usage: ${err}`);
        telemetry.trackError({
          message: 'Error fetching builder usage',
          error: fetchError,
        });
        setError(fetchError);
        setData(null);
      } finally {
        hasFetchedRef.current = true;
        setIsLoading(false);
      }
    },
    [apiUrl, requestContext.authToken, requestContext.builderId, telemetry],
  );

  useEffect(() => {
    if (!enabled) {
      // GPS disabled — do not fetch usage, and clear the initial loading state.
      setIsLoading(false);
      return;
    }
    const abortController = new AbortController();
    fetchUsage(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [enabled, fetchUsage]);

  const zusUserId = requestContext.authTokenState?.zusUserId;

  return useMemo(() => {
    const usageRecord = data?.included?.find(
      (
        item,
      ): item is Extract<NonNullable<BuilderApiResponse['included']>[number], { type: 'usage' }> =>
        item.type === 'usage' && item.userId === zusUserId,
    );

    return {
      monthlyLimit: data?.data.attributes.monthlyLimit ?? 0,
      summariesUsed: usageRecord?.attributes.freeSummariesGenerated ?? 0,
      billingTier: data?.data.attributes.billingTier ?? 'free',
      isLoading,
      error,
      refetch: fetchUsage,
    };
  }, [data, isLoading, error, fetchUsage, zusUserId]);
}
